import { useState, useEffect, useMemo } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { formatDate } from 'base/src/utils/stringUtil';
import { CampanhasService } from '../../../../services/campanha/campanhasService';
import PagarPedidoContainer from '../../../../containers/pagarPedido/pagarPedidoContainer';
import { LotePedidoResponse } from '../../../../types/lotePedido/lotePedidoResponse';
import { CampanhaPedidoResgateResponse } from '../../../../types/campanha/campanhaPedidoResgateResponse';
import { GerarPixResponse } from '../../../../types/lotePedido/gerarPixResponse';
import { GerarBoletoResponse } from '../../../../types/lotePedido/gerarBoletoResponse';
import { PagedListRequest } from 'domain/types/global/pagedListRequest';
import BarcodeDisplay from '../ModalAdicionarSaldo/components/BarcodeDisplay';
import { buildResumoHtml } from './buildResumoHtml';
import * as S from './ModalResumoLoteStyles';

export interface ModalResumoLoteProps {
  lote: LotePedidoResponse;
  campanhaId: number;
  carteiraId: number;
}

function getStatusVariant(statusId: number): 'warning' | 'primary' | 'positive' | 'danger' | 'neutral' {
  if (statusId === 1) return 'warning';
  if (statusId === 2) return 'primary';
  if (statusId === 3) return 'positive';
  if (statusId === 4) return 'danger';
  return 'neutral';
}

function getStatusLabel(statusId: number): string {
  if (statusId === 1) return 'Pendente';
  if (statusId === 2) return 'Processando';
  if (statusId === 3) return 'Pago';
  if (statusId === 4) return 'Excluido';
  if (statusId === 5) return 'Parcialmente Pago';
  return String(statusId);
}

function getItemStatusLabel(statusId: number | undefined): string {
  if (statusId === 1) return 'Solicitado';
  if (statusId === 2) return 'Aprovado';
  if (statusId === 3) return 'Não Aprovada';
  if (statusId === 4) return 'Pago/Entregue';
  if (statusId === 5) return 'Em Espera';
  if (statusId === 6) return 'Cancelado';
  if (statusId === 7) return 'Lote Alocado';
  if (statusId === 8) return 'Erro no Pagamento';
  return String(statusId ?? '-');
}

function ModalResumoLote(props: ModalResumoLoteProps) {
  const { lote, campanhaId, carteiraId } = props;
  const [users, setUsers] = useState<CampanhaPedidoResgateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pixData, setPixData] = useState<GerarPixResponse | null>(null);
  const [boletoData, setBoletoData] = useState<GerarBoletoResponse | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(true);

  useEffect(function loadData() {
    const service = new CampanhasService();
    const container = new PagarPedidoContainer();

    async function fetchUsers() {
      try {
        const request: PagedListRequest<CampanhaPedidoResgateResponse> = {
          skip: 0,
          take: 9999,
          search: '',
          filter: [],
          data: { CampanhaPedidoResgatePagamentoID: lote.CampanhaPedidoResgatePagamentoID } as CampanhaPedidoResgateResponse
        };

        const result = await service.GetPedidoResgatesByLote(
          lote.CampanhaPedidoResgatePagamentoID,
          campanhaId,
          request
        );

        if (result instanceof Error) {
          setError('Erro ao carregar usuarios do lote');
        } else {
          setUsers(result.Result || []);
        }
      } catch {
        setError('Erro ao carregar usuarios do lote');
      } finally {
        setLoading(false);
      }
    }

    async function fetchPaymentData() {
      try {
        const [pixResult, boletoResult] = await Promise.allSettled([
          container.gerarPix(lote.CampanhaPedidoResgatePagamentoID, carteiraId),
          container.gerarBoleto(lote.CampanhaPedidoResgatePagamentoID, carteiraId)
        ]);

        if (pixResult.status === 'fulfilled' && !(pixResult.value instanceof Error)) {
          setPixData(pixResult.value);
        }

        if (boletoResult.status === 'fulfilled' && !(boletoResult.value instanceof Error)) {
          setBoletoData(boletoResult.value);
        }
      } catch {
        // Payment data is optional
      } finally {
        setLoadingPayment(false);
      }
    }

    fetchUsers();
    fetchPaymentData();
  }, [lote.CampanhaPedidoResgatePagamentoID, campanhaId, carteiraId]);

  const htmlContent = useMemo(function buildHtml() {
    if (loading || error || loadingPayment) return '';
    return buildResumoHtml(lote, users, pixData, boletoData);
  }, [loading, error, loadingPayment, lote, users, pixData, boletoData]);

  function handlePrint() {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Aguardar imagens carregarem antes de imprimir
    const images = printWindow.document.querySelectorAll('img');
    if (images.length === 0) {
      printWindow.focus();
      printWindow.print();
      return;
    }

    let loaded = 0;
    function onImageReady() {
      loaded++;
      if (loaded >= images.length && printWindow) {
        printWindow.focus();
        printWindow.print();
      }
    }

    images.forEach(function waitImage(img) {
      if (img.complete) {
        onImageReady();
      } else {
        img.addEventListener('load', onImageReady);
        img.addEventListener('error', onImageReady);
      }
    });
  }

  function handleDownload() {
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `resumo_lote_${lote.CampanhaPedidoResgatePagamentoID}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <S.Container>
      <S.Header>
        <S.Title>Resumo do Lote</S.Title>
      </S.Header>

      <S.Content>
        {loading && (
          <S.LoadingContainer>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Carregando resumo...
            </Typography>
          </S.LoadingContainer>
        )}

        {error && <S.ErrorMessage>{error}</S.ErrorMessage>}

        {!loading && !error && (
          <>
            {/* Header do Lote */}
            <S.InfoGrid>
              <S.InfoItem>
                <S.InfoLabel>Lote</S.InfoLabel>
                <S.InfoValue>{lote.Nome}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Data</S.InfoLabel>
                <S.InfoValue>{formatDate(lote.RealizadoEm)}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Numero</S.InfoLabel>
                <S.InfoValue>{lote.NumeroLote || '-'}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Status</S.InfoLabel>
                <Tag variant={getStatusVariant(lote.CampanhaPedidoResgatePagamentoStatusID)}>
                  {getStatusLabel(lote.CampanhaPedidoResgatePagamentoStatusID)}
                </Tag>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Realizado por</S.InfoLabel>
                <S.InfoValue>{lote.RealizadoPor || lote.Usuario || '-'}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Usuarios</S.InfoLabel>
                <S.InfoValue>{lote.QtdeUsuarios}</S.InfoValue>
              </S.InfoItem>
            </S.InfoGrid>

            {/* Lista de Usuarios */}
            <S.SectionTitle>Usuarios ({users.length})</S.SectionTitle>
            <S.TableContainer>
              <S.UserTable>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Usuario</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(function renderUserRow(user, index) {
                    return (
                      <tr key={user.CampanhaPedidoResgateID}>
                        <td>{index + 1}</td>
                        <td>{user.UsuarioNome || user.Usuario || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{formatToBRL(user.ValorOriginal ?? 0)}</td>
                        <td>{getItemStatusLabel(user.CampanhaPedidoResgateStatusID ?? user.Status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </S.UserTable>
            </S.TableContainer>

            {/* Resumo Financeiro */}
            <S.SectionTitle>Resumo Financeiro</S.SectionTitle>
            <S.SummaryGrid>
              <S.SummaryRow>
                <S.SummaryLabel>Valor Original</S.SummaryLabel>
                <S.SummaryValue>{formatToBRL(lote.ValorOriginal)}</S.SummaryValue>
              </S.SummaryRow>
              <S.SummaryRow>
                <S.SummaryLabel>Taxa</S.SummaryLabel>
                <S.SummaryValue>{formatToBRL(lote.ValorTaxa)}</S.SummaryValue>
              </S.SummaryRow>
              <S.SummaryRow isTotal>
                <S.SummaryLabel isTotal>Valor Total</S.SummaryLabel>
                <S.SummaryValue isTotal>{formatToBRL(lote.ValorTotal)}</S.SummaryValue>
              </S.SummaryRow>
            </S.SummaryGrid>

            <S.Divider />

            {/* Pagamento - QR Code e Barcode lado a lado */}
            <S.SectionTitle>Pagamento</S.SectionTitle>

            {loadingPayment && (
              <S.LoadingContainer>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Gerando dados de pagamento...
                </Typography>
              </S.LoadingContainer>
            )}

            {!loadingPayment && (
              <S.PaymentRow>
                {/* PIX QR Code */}
                {pixData && (
                  <S.PaymentBlockPix>
                    <S.QrCodeImage
                      src={`data:image/png;base64,${pixData.QrCode}`}
                      alt="QR Code PIX"
                    />
                    <S.PaymentBlockTitle>PIX</S.PaymentBlockTitle>
                  </S.PaymentBlockPix>
                )}

                {/* Boleto Barcode */}
                {boletoData && boletoData.CodBarras && (
                  <S.PaymentBlockBoleto>
                    <S.BarcodeContainer>
                      <BarcodeDisplay value={boletoData.CodBarras} />
                    </S.BarcodeContainer>
                    <S.BarcodeLabel>Boleto</S.BarcodeLabel>
                    <S.BarcodeValue>{boletoData.CodBarras}</S.BarcodeValue>
                  </S.PaymentBlockBoleto>
                )}

                {!pixData && !boletoData && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, width: '100%' }}>
                    Nao foi possivel gerar os dados de pagamento.
                  </Typography>
                )}
              </S.PaymentRow>
            )}

            {!loadingPayment && (
              <S.ActionBar>
                <Button size="sm" iconName="ph:printer" onClick={handlePrint}>
                  Imprimir Resumo
                </Button>
              </S.ActionBar>
            )}
          </>
        )}
      </S.Content>
    </S.Container>
  );
}

export default ModalResumoLote;
